import React, { useCallback } from "react";
import { OutreachEmailDraft } from "../types";

interface OutreachEmailCardProps {
  draft: OutreachEmailDraft;
  /** Called on every subject/body edit and on Approve — parent applies the update immutably. */
  onChange?: (next: OutreachEmailDraft) => void;
  /** Sends a new chat message asking the model to redraft (reuses the existing chat-action path). */
  onRegenerate?: () => void;
  isLoading?: boolean;
}

const CTA_STYLE_LABEL: Record<OutreachEmailDraft["cta_style"], string> = {
  hybrid: "Booking link + reply option",
  booking_link: "Booking link only",
  reply_only: "Reply only",
};

/**
 * Inline chat card for the Outreach Email chip (SGL-83): an editable subject + body
 * draft the customer reviews before anything sends. Nothing sends from here — Approve
 * only freezes the draft read-only; OutreachCampaignFlow renders directly beneath this
 * card and owns recipient selection, the send confirmation, and campaign progress.
 * Mirrors BlogReadyCard's placement but the box itself is editable, not a link out to
 * a separate canvas — there is no canvas for this chip yet.
 */
export const OutreachEmailCard: React.FC<OutreachEmailCardProps> = ({
  draft,
  onChange,
  onRegenerate,
  isLoading = false,
}) => {
  const approved = Boolean(draft.approved);

  const handleSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (approved) return;
      onChange?.({ ...draft, subject: e.target.value });
    },
    [approved, draft, onChange]
  );

  const handleBodyChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (approved) return;
      onChange?.({ ...draft, body: e.target.value });
    },
    [approved, draft, onChange]
  );

  const handleApprove = useCallback(() => {
    onChange?.({ ...draft, approved: true });
  }, [draft, onChange]);

  return (
    <div className="my-3 w-full min-w-0 max-w-full rounded-2xl border border-theme-border bg-theme-surface-ui p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-theme-secondary/20">
            <span className="material-icons text-[18px] text-theme-primary">mail</span>
          </span>
          <p className="truncate text-[13px] font-bold text-theme-fg">
            Outreach Email Draft
          </p>
        </div>
        <span
          className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
            approved
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-theme-secondary/20 text-theme-fg-muted"
          }`}
        >
          {approved ? "Approved ✓" : "Draft"}
        </span>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
          Subject
        </label>
        <input
          type="text"
          value={draft.subject}
          onChange={handleSubjectChange}
          readOnly={approved}
          placeholder="Email subject"
          className="w-full rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-fg focus:border-theme-primary focus:outline-none disabled:cursor-default read-only:cursor-default read-only:opacity-80"
        />
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
          Body
        </label>
        <textarea
          value={draft.body}
          onChange={handleBodyChange}
          readOnly={approved}
          rows={8}
          placeholder="Email body"
          className="w-full resize-y rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm leading-relaxed text-theme-fg whitespace-pre-wrap focus:border-theme-primary focus:outline-none read-only:cursor-default read-only:opacity-80"
        />
      </div>

      {(draft.merge_fields?.length > 0 || draft.booking_link) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {draft.merge_fields.map((field) => (
            <span
              key={field}
              className="rounded-md bg-theme-secondary/15 px-2 py-0.5 text-[11px] font-mono text-theme-fg-muted"
            >
              {`{{${field}}}`}
            </span>
          ))}
          {draft.booking_link && (
            <a
              href={draft.booking_link}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-theme-secondary/15 px-2 py-0.5 text-[11px] text-theme-primary hover:underline"
            >
              {draft.booking_link}
            </a>
          )}
          <span className="text-[11px] text-theme-fg-muted">
            · {CTA_STYLE_LABEL[draft.cta_style]}
          </span>
        </div>
      )}

      {draft.notes && (
        <p className="mt-2 text-[11px] italic text-theme-fg-muted">{draft.notes}</p>
      )}

      {!approved && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onRegenerate?.()}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-xl border border-theme-border px-3 py-1.5 text-[12px] font-semibold text-theme-fg transition-colors hover:bg-theme-secondary/15 disabled:cursor-default disabled:opacity-60"
          >
            <span className="material-icons text-[14px]">refresh</span>
            Regenerate
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-xl bg-theme-primary px-3 py-1.5 text-[12px] font-bold text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-default disabled:opacity-60"
          >
            <span className="material-icons text-[14px]">check</span>
            Approve
          </button>
        </div>
      )}
    </div>
  );
};
